import { getStorageInstance } from '@/firebaseInit'

// Delete one object by storage path. firebase/storage is imported dynamically
// so this util (pulled in eagerly via main.js) keeps the SDK off the visitor
// critical-path bundle.
async function deleteByPath(path) {
  const [storage, { ref: storageRef, deleteObject }] = await Promise.all([
    getStorageInstance(),
    import('firebase/storage')
  ])
  await deleteObject(storageRef(storage, path))
}

/**
 * Extract the Storage path from a Firebase Storage download URL.
 * Returns null if the URL is not a valid Firebase Storage URL.
 */
export function getStoragePathFromUrl(url) {
  if (!url || !url.includes('firebasestorage.googleapis.com')) return null
  const pathMatch = decodeURIComponent(url).match(/\/o\/(.+?)\?/)
  return pathMatch ? pathMatch[1] : null
}

/**
 * Delete a single file from Firebase Storage by its download URL.
 * Silently catches errors (file may already be deleted).
 */
export async function deleteStorageFileByUrl(url) {
  const path = getStoragePathFromUrl(url)
  if (!path) return
  try {
    await deleteByPath(path)
  } catch (error) {
    console.error('Failed to delete storage file:', error)
  }
}

/**
 * Delete a single file from Firebase Storage by its storage path.
 * Used by PersonalGalleryAdmin, UserAdmin etc. that store raw paths.
 */
export async function deleteStorageFile(path) {
  if (!path) return
  try {
    await deleteByPath(path)
  } catch (error) {
    console.error('Failed to delete storage file:', error)
  }
}

/**
 * Delete multiple files from Firebase Storage by their download URLs.
 * Errors on individual files don't stop the rest from being deleted.
 */
export async function deleteStorageFilesByUrl(urls) {
  for (const url of urls) {
    await deleteStorageFileByUrl(url)
  }
}

/**
 * Extract all Firebase Storage URLs from a block's data.
 * Supports image, document, and timeline block types.
 */
export function getStorageUrls(block) {
  const urls = []
  const data = block.data || {}

  if (block.type === 'image' && data.imageUrl) {
    urls.push(data.imageUrl)
  }
  if (block.type === 'document' && Array.isArray(data.documents)) {
    data.documents.forEach(doc => {
      if (doc.fileUrl) urls.push(doc.fileUrl)
    })
  }
  if (block.type === 'timeline' && Array.isArray(data.events)) {
    data.events.forEach(event => {
      if (event.thumbnail) urls.push(event.thumbnail)
    })
  }
  if (block.type === 'columns' && Array.isArray(data.slots)) {
    data.slots.forEach(slot => {
      if (slot) urls.push(...getStorageUrls(slot))
    })
  }

  return urls.filter(url => url.includes('firebasestorage.googleapis.com'))
}
